/**
 * One item of the board, drawn as a plain element at its place in board units (the layer
 * around it pans and zooms them all): a sticky note, free text, a shape, a picture from the
 * project's files, UI/UX's elements (a button, an input, a checkbox, a list, an instance of a
 * component, drawn like the prototype draws them, but not working), or a card for any other
 * kind. Its look is its `style` (`shared/elements.ts`; a picture's `crop` is the part of it shown); a double click writes its words in place
 * (`Editable`), sent when the field is left; on an instance, a part's own words
 * (`[data-instance-part]`).
 */
import { For, Match, Show, Switch, createMemo, type JSX } from "solid-js";
import type { BoardPlace } from "@protocol/BoardPlace";
import type { Node } from "@protocol/Node";
import { useApp } from "../../../app/context";
import { pictureOf } from "../../../shared/assets";
import { itemShape, lookOf, frameLook, showsWords } from "../../../shared/elements";
import { inkPoints, instanceParts, overrideKey, parseCrop, smoothPath, type InstancePart } from "../../../state/board";
import type { Run } from "../../../shared/richText";
import { Editable } from "./Editable";
import { placeCss, styleCss } from "./look";

export { itemShape };

/**
 * A line drawn by hand (`style.path`, `stroke`, `width`), stretched with its box. Only its stroke
 * catches the pointer, through a wider see-through copy (`[data-drawing]`, what the eraser looks for).
 */
function Drawing(props: { id: string; place: BoardPlace; style: Record<string, string> }): JSX.Element {
  const d = createMemo(() => smoothPath(inkPoints(props.style.path ?? "", props.place.w, props.place.h)));
  const width = () => Math.max(0.5, Number(props.style.width) || 4);
  return (
    <svg viewBox={`0 0 ${props.place.w} ${props.place.h}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={d()} fill="none" stroke="transparent" stroke-width={Math.max(width() * 2, 12)} stroke-linecap="round" stroke-linejoin="round" data-drawing={props.id} />
      <path d={d()} fill="none" stroke={props.style.stroke || "#1d1b26"} stroke-width={width()} stroke-linecap="round" stroke-linejoin="round" data-drawing={props.id} />
    </svg>
  );
}

/** A picture of the project's files, or its name while there is none; `crop`: the part shown (`style.crop`). */
function Picture(props: { attachments: readonly string[] | undefined; name: string; fit?: string; crop?: string }): JSX.Element {
  const { assets } = useApp();
  const src = createMemo(() => {
    const path = pictureOf(props.attachments);
    return path ? assets.url(path) : null;
  });
  return (
    <Show when={src()} fallback={<div class="board-picture-missing">{props.name}</div>}>
      {(s) => {
        const part = () => parseCrop(props.crop);
        // Cropped: the whole picture, bigger than the item, moved so the part kept fills it.
        const css = (): JSX.CSSProperties => {
          const c = part();
          return c
            ? { position: "absolute", "max-width": "none", width: `${100 / c.w}%`, height: `${100 / c.h}%`, left: `${(-c.x / c.w) * 100}%`, top: `${(-c.y / c.h) * 100}%`, "object-fit": "fill" }
            : { "object-fit": props.fit === "contain" ? "contain" : "cover" };
        };
        return <img src={s()} alt={props.name} draggable={false} style={css()} />;
      }}
    </Show>
  );
}

/** What a UI element shows inside (not editable: its words are written by a double click on the item). */
function ElementBody(props: { kind: string; text: string; style: { readonly [key in string]?: string }; attachments?: readonly string[] }): JSX.Element {
  const shape = () => itemShape(props.kind);
  return (
    <Switch fallback={<Show when={showsWords(shape(), props.kind)}>{props.text}</Show>}>
      <Match when={shape() === "image"}>
        <Picture attachments={props.attachments} name={props.text} fit={props.style.fit} />
      </Match>
      <Match when={shape() === "input"}>
        <span class="board-hint">{props.style.secret === "yes" ? "••••••" : props.text}</span>
      </Match>
      <Match when={shape() === "checkbox"}>
        <span class="board-tick" classList={{ on: props.style.checked === "yes" }} />
        <span>{props.text}</span>
      </Match>
      <Match when={shape() === "list"}>
        <For each={props.text.split("\n").filter((l) => l.trim())}>{(line) => <div class="board-row">{line}</div>}</For>
      </Match>
    </Switch>
  );
}

/** An instance's parts, placed inside it. */
function Parts(props: { parts: InstancePart[]; editingPart: string | null; write: (part: string, text: string) => void; done: () => void }): JSX.Element {
  return (
    <For each={props.parts}>
      {(part) => {
        const shape = () => (part.node ? itemShape(part.node.kind) : null);
        const css = (): JSX.CSSProperties => ({ ...placeCss(part.place), ...((shape() ? lookOf(shape()!, part.style) : frameLook(part.style)) as JSX.CSSProperties) });
        return (
          <div class={`board-part board-${shape() ?? "box"}`} classList={{ [`shape-${part.style.shape ?? "rectangle"}`]: shape() === "shape" }} data-instance-part={part.id} style={css()}>
            <Show when={part.node}>
              {(node) => (
                <Show
                  when={props.editingPart === part.id}
                  fallback={
                    <Show when={part.parts.length} fallback={<ElementBody kind={node().kind} text={part.text} style={part.style} attachments={node().attachments} />}>
                      <Parts parts={part.parts} editingPart={null} write={props.write} done={props.done} />
                    </Show>
                  }
                >
                  <Editable text={part.text} editing={true} done={props.done} write={(text) => props.write(part.id, text)} />
                </Show>
              )}
            </Show>
          </div>
        );
      }}
    </For>
  );
}

/** One item. `editing`: its words are being written (`editingPart`: an instance part's); `done` ends it. */
export function BoardItem(props: { id: string; node: Node; place: BoardPlace; selected: boolean; editing: boolean; editingPart?: string | null; done: () => void; editAt?: { x: number; y: number } | null; hidden?: boolean }): JSX.Element {
  const { board, kinds, graph, actions } = useApp();
  const shape = () => itemShape(props.node.kind);
  const style = () => props.node.style ?? {};
  const instance = createMemo(() => (shape() === "instance" ? instanceParts(graph.state.graph, board.content(), props.node, props.place) : null));
  const css = createMemo((): JSX.CSSProperties => ({ ...placeCss(props.place), ...styleCss(shape(), style()), ...(instance() ? (frameLook(instance()!.frame) as JSX.CSSProperties) : {}) }));
  /** Writes an instance's own words for a part (the component's words again: the override goes). */
  const writePart = (part: string, text: string) => {
    const own = instance()?.parts.find((p) => p.id === part);
    const original = own?.node?.name ?? "";
    const next = text.replace(/\s+$/, "");
    if (next === (style()[overrideKey(part)] ?? original)) return;
    void actions.updateNode(props.id, { style: { [overrideKey(part)]: next === original ? "" : next } });
  };
  const write = (text: string) => board.setText(props.id, text);
  /** Sends words with looks of their own (`style.rich`). */
  const writeRich = (runs: readonly Run[], cleared: string[]) => void board.setRich(props.id, runs, cleared);
  return (
    <div
      class={`board-item board-${shape()}`}
      classList={{ "crop-hidden": !!props.hidden, selected: props.selected, ui: props.node.kind.startsWith("ui_"), [`shape-${style().shape ?? "rectangle"}`]: shape() === "shape", leads: !!style().navigate && board.prototyping() }}
      data-item={props.id}
      data-kind={props.node.kind}
      style={css()}
      title={shape() === "image" || shape() === "instance" ? props.node.name : undefined}
    >
      <Switch>
        <Match when={shape() === "image"}>
          <Picture attachments={props.node.attachments} name={props.node.name} fit={style().fit} crop={style().crop} />
        </Match>
        <Match when={shape() === "drawing"}>
          <Drawing id={props.id} place={props.place} style={style()} />
        </Match>
        <Match when={shape() === "instance"}>
          <Show when={instance()} fallback={<div class="board-picture-missing">{props.node.name}: its component is gone</div>}>
            {(made) => <Parts parts={made().parts} editingPart={props.editingPart ?? null} write={writePart} done={props.done} />}
          </Show>
        </Match>
        <Match when={shape() === "card"}>
          <div class="board-card-kind">{kinds.ofNode(props.node).label}</div>
          <Editable text={props.node.name} editing={props.editing} done={props.done} write={write} at={props.editAt} id={props.id} rich={style().rich} whole={style()} writeRich={writeRich} />
        </Match>
        <Match when={props.editing}>
          <Editable text={props.node.name} editing={true} done={props.done} write={write} at={props.editAt} id={props.id} rich={style().rich} whole={style()} writeRich={writeRich} />
        </Match>
        <Match when={shape() === "note" || shape() === "text" || (shape() === "shape" && showsWords("shape", props.node.kind))}>
          <Editable text={props.node.name} editing={false} done={props.done} write={write} id={props.id} rich={style().rich} whole={style()} writeRich={writeRich} />
          <Show when={shape() === "note" && props.node.note}>
            <div class="board-note-more">{props.node.note}</div>
          </Show>
        </Match>
        <Match when={true}>
          <ElementBody kind={props.node.kind} text={props.node.name} style={style()} />
        </Match>
      </Switch>
      <Show when={props.node.comments.length}>
        <span class="board-comments" title={`${props.node.comments.length} comment(s): the inspector shows them`}>
          {props.node.comments.length}
        </span>
      </Show>
    </div>
  );
}
